// ============================================================
// PPT 导出
// ============================================================
import { uploadPptExportAsset, importPptExportAsset } from '@/api/ppt'
import { renderRasterHtml } from '@/utils/pptRender'

const ASSET_SCHEME = 'pptasset://'
const INLINE_DATA_URI_MAX = 4096 // 小图/占位SVG保持内联，不上传

// data URI mime → 文件后缀（上传文件名用，jpeg 归一为 jpg）
const MIME_EXT = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
  'image/bmp': 'bmp',
  'audio/mpeg': 'mp3',
  'audio/mp3': 'mp3',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
  'audio/ogg': 'ogg',
  'audio/aac': 'aac',
  'audio/mp4': 'm4a',
  'audio/x-m4a': 'm4a',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/ogg': 'ogv'
}

// 单个资源 URL → 资源编号 key；不需要上传的（已是钥匙/小 data URI/非 OSS 外链）返回 null 保持原样
async function resolveAssetKey(url) {
  // 已是私有桶钥匙：跳过
  if (url.indexOf(ASSET_SCHEME) === 0) return null
  // data URI：小体积保持内联；大体积（内嵌 base64 图/音视频）转 Blob 上传私有桶
  if (url.indexOf('data:') === 0) {
    if (url.length <= INLINE_DATA_URI_MAX) return null
    const semi = url.indexOf(';')
    const comma = url.indexOf(',')
    const mime = url.slice(5, semi > 0 ? semi : comma)
    const blob = await fetch(url).then((r) => r.blob())
    const file = new File([blob], 'ppt-asset.' + (MIME_EXT[mime] || 'bin'), { type: mime || 'application/octet-stream' })
    const res = await uploadPptExportAsset(file)
    if (!res || !res.data || res.data.code !== 0) {
      throw new Error((res && res.data && res.data.message) || '资源上传失败')
    }
    return res.data.data
  }
  // eltmax 现有 OSS 桶直链：服务器端拷贝进私有桶（不在浏览器落地）
  if (url.indexOf('aliyuncs.com') !== -1) {
    const res = await importPptExportAsset(url)
    if (!res || !res.data || res.data.code !== 0) {
      throw new Error((res && res.data && res.data.message) || '资源导入失败')
    }
    return res.data.data
  }
  return null
}

// 收集 pages 中全部资源 URL（去重）：frames/img/videos[].url/videoUrl/audio/audios[].url/audioMedia
function collectAssetUrls(pages) {
  const urls = []
  const seen = new Set()
  const push = (u) => {
    if (typeof u !== 'string' || !u || seen.has(u)) return
    seen.add(u)
    urls.push(u)
  }
  pages.forEach((p) => {
    if (Array.isArray(p.frames)) p.frames.forEach(push)
    push(p.img)
    const videoList = Array.isArray(p.videos) && p.videos.length
      ? p.videos
      : (p.videoUrl ? [{ url: p.videoUrl }] : [])
    videoList.forEach((v) => push(v && v.url))
    push(p.audio)
    const audioList = Array.isArray(p.audios) && p.audios.length
      ? p.audios
      : (p.audioMedia ? [{ url: p.audioMedia }] : [])
    audioList.forEach((au) => push(au && au.url))
    push(p.audioMedia)
  })
  return urls
}

// 深拷贝 pages 并把资源 URL 替换为 pptasset://key（入参可能是冻结对象，必须克隆后再改）
function replaceAssetUrls(pages, resourceMap) {
  const cloned = JSON.parse(JSON.stringify(pages || []))
  const swap = (u) => (resourceMap.has(u) ? resourceMap.get(u) : u)
  cloned.forEach((p) => {
    if (Array.isArray(p.frames)) p.frames = p.frames.map(swap)
    if (p.img) p.img = swap(p.img)
    if (Array.isArray(p.videos)) {
      p.videos.forEach((v) => { if (v && v.url) v.url = swap(v.url) })
    }
    if (p.videoUrl) p.videoUrl = swap(p.videoUrl)
    if (p.audio) p.audio = swap(p.audio)
    if (Array.isArray(p.audios)) {
      p.audios.forEach((au) => { if (au && au.url) au.url = swap(au.url) })
    }
    if (p.audioMedia) p.audioMedia = swap(p.audioMedia)
  })
  return cloned
}

// 受保护导出前置：pages 中的资源全部收进私有桶
// @returns { pages: 深拷贝且资源URL已替换为 pptasset://key, keys: 新生成的资源编号数组（去重、保持生成顺序） }
export async function prepareProtectedPages(pages, onProgress) {
  const list = Array.isArray(pages) ? pages : []
  const urls = collectAssetUrls(list)
  const resourceMap = new Map()
  const keys = []
  for (let i = 0; i < urls.length; i++) {
    const key = await resolveAssetKey(urls[i])
    if (key) {
      resourceMap.set(urls[i], ASSET_SCHEME + key)
      keys.push(key)
    }
    if (onProgress) onProgress(i + 1, urls.length)
  }
  return { pages: replaceAssetUrls(list, resourceMap), keys }
}

function downloadHtml(html, filename) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// 受保护版导出：资源进私有桶，页内只留 pptasset:// 钥匙，下载「课件名.html」（传 eOnline 平台用）
// options.watermark：可选水印，字符串或 { text, mode: tile/corner/top, corners: [tl,tr,bl,br], density }，留空不加水印
export async function exportProtectedHtml(ppt, options = {}) {
  const title = ppt.title || 'ppt'
  const prep = await prepareProtectedPages(ppt.pages, options.onProgress)
  const html = renderRasterHtml(
    { title, pageW: ppt.pageW, pageH: ppt.pageH, pages: prep.pages },
    { antiCopy: true, showNav: true, showFull: true, protected: true, assetKeys: prep.keys, watermark: options.watermark || '' }
  )
  downloadHtml(html, title + '.html')
}

// 验证版导出：现有自包含导出 + 全屏水印，下载「课件名-验证版.html」（本地双击验证用，勿上传 eOnline）
export async function exportVerifyHtml(ppt, options = {}) {
  const title = ppt.title || 'ppt'
  const wm = options.watermark
  const html = renderRasterHtml(
    { title, pageW: ppt.pageW, pageH: ppt.pageH, pages: ppt.pages },
    { antiCopy: true, showNav: true, showFull: true, watermark: wm || '验证专用·禁止上传' }
  )
  downloadHtml(html, title + '-验证版.html')
}
